import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useApi, useAuth } from "@/lib/auth";
import PageHeader from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { fmtEventDateTime, formatPrice } from "@/lib/format";
import { Users, ShieldCheck, Upload, CreditCard, Plus, ChevronRight, Database, Ticket } from "lucide-react";

function isClosed(e) {
  const now = Date.now();
  if (e.bookings_close_at) {
    const t = new Date(e.bookings_close_at).getTime();
    if (!isNaN(t) && t <= now) return true;
  }
  const s = new Date(e.starts_at).getTime();
  return !isNaN(s) && s <= now;
}

function EventRow({ e }) {
  return (
    <Link to={`/admin/events/${e.event_id}/bookings`} data-testid={`admin-event-${e.event_id}`}
          className="flex items-start justify-between gap-3 p-4 border-2 border-red-500/60 rounded-md hover:bg-red-50 transition-colors">
      <div>
        <div className="font-semibold">{e.name}</div>
        <div className="text-sm text-muted-foreground">{fmtEventDateTime(e.starts_at)}</div>
        <div className="flex gap-2 mt-2 flex-wrap">
          <Badge variant="outline">{e.stripe_account_label}</Badge>
          <Badge variant="outline">{formatPrice(e.price_pence, e.currency)}</Badge>
          <Badge variant="outline">Cap. {e.capacity}</Badge>
          {isClosed(e) && <Badge variant="destructive">Bookings closed</Badge>}
        </div>
      </div>
      <ChevronRight className="text-muted-foreground shrink-0 mt-1" size={18} />
    </Link>
  );
}

export default function AdminPage() {
  const api = useApi();
  const { user } = useAuth();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const r = await api("/events");
        if (r.ok) { const d = await r.json(); setEvents(d.events || []); }
      } finally { setLoading(false); }
    })();
    // eslint-disable-next-line
  }, []);

  const { active, closed } = useMemo(() => {
    const a = [], c = [];
    for (const e of events) (isClosed(e) ? c : a).push(e);
    a.sort((x, y) => new Date(x.starts_at) - new Date(y.starts_at));
    c.sort((x, y) => new Date(y.starts_at) - new Date(x.starts_at));
    return { active: a, closed: c };
  }, [events]);

  return (
    <div>
      <PageHeader title="Admin" subtitle={user?.role === "super_admin" ? "Super Admin access" : "Admin access"} testId="admin-page-header" />

      <div className="border-2 border-red-500/60 rounded-lg p-5 bg-card mb-6" data-testid="admin-section-events">
        <h2 className="text-lg font-semibold text-red-600">Event Management</h2>
        <p className="text-sm text-muted-foreground">
          New events are always created from a template so they can be reused year on year.
        </p>
        <div className="mt-4 flex gap-3 flex-wrap">
          <Link to="/admin/templates" data-testid="admin-new-event">
            <Button className="min-w-[180px]"><Plus size={16} className="mr-2" /> New event</Button>
          </Link>
        </div>

        <h3 className="font-semibold text-red-600 mt-6 mb-3" data-testid="admin-active-heading">Active events</h3>
        {loading ? <div className="text-sm text-muted-foreground">Loading…</div> :
          active.length === 0 ? <div className="text-sm text-muted-foreground italic">No active events. Tap "New event" to create one.</div> :
          <div className="grid gap-3 md:grid-cols-2">{active.map((e) => <EventRow key={e.event_id} e={e} />)}</div>}

        <h3 className="font-semibold text-red-600 mt-6 mb-3" data-testid="admin-closed-heading">Closed bookings</h3>
        {loading ? null : closed.length === 0 ?
          <div className="text-sm text-muted-foreground italic">No events with closed bookings yet.</div> :
          <div className="grid gap-3 md:grid-cols-2">{closed.map((e) => <EventRow key={e.event_id} e={e} />)}</div>}
      </div>

      {/* Complimentary Ticket Management — positioned between Event Management
          and Member Management per the spec. Same visual pattern as the other
          admin sections (red-outlined card + tile grid). */}
      <div className="border-2 border-red-500/60 rounded-lg p-5 bg-card mb-6" data-testid="admin-section-comp-tickets">
        <h2 className="text-lg font-semibold text-red-600">Complimentary Ticket Management</h2>
        <p className="text-sm text-muted-foreground">
          Maintain the Province &amp; District master list used for complimentary ticket allocation.
        </p>
        <div className="mt-4 grid gap-3 grid-cols-1 sm:grid-cols-3">
          <Link to="/admin/province-lc" data-testid="admin-province-lc">
            <Card className="p-4 border-2 border-red-500/60 hover:bg-red-50 transition-colors">
              <Ticket className="text-red-600" size={22} />
              <div className="mt-2 font-semibold">Province &amp; District master</div>
              <div className="text-sm text-muted-foreground">Add, edit, delete entries</div>
            </Card>
          </Link>
          <Link to="/admin/craft-master" data-testid="admin-craft-master">
            <Card className="p-4 border-2 border-red-500/60 hover:bg-red-50 transition-colors">
              <Ticket className="text-red-600" size={22} />
              <div className="mt-2 font-semibold">Middlesex Craft Province master</div>
              <div className="text-sm text-muted-foreground">Add, edit, delete entries</div>
            </Card>
          </Link>
          <Link to="/admin/royal-arch-master" data-testid="admin-royal-arch-master">
            <Card className="p-4 border-2 border-red-500/60 hover:bg-red-50 transition-colors">
              <Ticket className="text-red-600" size={22} />
              <div className="mt-2 font-semibold">Middlesex Royal Arch Province master</div>
              <div className="text-sm text-muted-foreground">Add, edit, delete entries</div>
            </Card>
          </Link>
        </div>
        {/* Email templates — one per master list. Phase 1: edit + save
            only; Phase 2 will wire the actual send flow. */}
        <h3 className="mt-6 text-sm font-semibold text-red-600 uppercase tracking-wide">
          Email templates
        </h3>
        <div className="mt-2 grid gap-3 grid-cols-1 sm:grid-cols-3">
          <Link to="/admin/comp-email-templates/province_lc" data-testid="admin-comp-email-tpl-province_lc">
            <Card className="p-4 border-2 border-red-500/60 hover:bg-red-50 transition-colors">
              <Ticket className="text-red-600" size={22} />
              <div className="mt-2 font-semibold">Email — Prov &amp; Dist</div>
              <div className="text-sm text-muted-foreground">Edit subject &amp; body</div>
            </Card>
          </Link>
          <Link to="/admin/comp-email-templates/middlesex_craft" data-testid="admin-comp-email-tpl-middlesex_craft">
            <Card className="p-4 border-2 border-red-500/60 hover:bg-red-50 transition-colors">
              <Ticket className="text-red-600" size={22} />
              <div className="mt-2 font-semibold">Email — PGLM (Craft)</div>
              <div className="text-sm text-muted-foreground">Edit subject &amp; body</div>
            </Card>
          </Link>
          <Link to="/admin/comp-email-templates/middlesex_royal_arch" data-testid="admin-comp-email-tpl-middlesex_royal_arch">
            <Card className="p-4 border-2 border-red-500/60 hover:bg-red-50 transition-colors">
              <Ticket className="text-red-600" size={22} />
              <div className="mt-2 font-semibold">Email — PGCM (Royal Arch)</div>
              <div className="text-sm text-muted-foreground">Edit subject &amp; body</div>
            </Card>
          </Link>
        </div>
      </div>

      <div className="border-2 border-red-500/60 rounded-lg p-5 bg-card mb-6" data-testid="admin-section-members">
        <h2 className="text-lg font-semibold text-red-600">Members Management</h2>
        <p className="text-sm text-muted-foreground">Search accounts, approve new signups, and keep the member directory up to date.</p>
        <div className="mt-4 grid gap-3 grid-cols-1 sm:grid-cols-3">
          <Link to="/admin/users" data-testid="admin-users">
            <Card className="p-4 border-2 border-red-500/60 hover:bg-red-50 transition-colors">
              <Users className="text-red-600" size={22} />
              <div className="mt-2 font-semibold">Users</div>
              <div className="text-sm text-muted-foreground">Search &amp; manage</div>
            </Card>
          </Link>
          <Link to="/admin/review-queue" data-testid="admin-review-queue">
            <Card className="p-4 border-2 border-red-500/60 hover:bg-red-50 transition-colors">
              <ShieldCheck className="text-red-600" size={22} />
              <div className="mt-2 font-semibold">Review queue</div>
              <div className="text-sm text-muted-foreground">Approve new signups</div>
            </Card>
          </Link>
          <Link to="/admin/directory-import" data-testid="admin-directory">
            <Card className="p-4 border-2 border-red-500/60 hover:bg-red-50 transition-colors">
              <Upload className="text-red-600" size={22} />
              <div className="mt-2 font-semibold">Import members</div>
              <div className="text-sm text-muted-foreground">Craft + RA CSVs</div>
            </Card>
          </Link>
        </div>
      </div>

      {user?.role === "super_admin" && (
        <div className="border-2 border-red-500/60 rounded-lg p-5 bg-card mb-6">
          <h2 className="text-lg font-semibold text-red-600">Stripe Accounts / Event Hosts</h2>
          <p className="text-sm text-muted-foreground">
            Each Event Host maps a Stripe account to its Live and Test credentials.
          </p>
          <div className="mt-4 flex flex-wrap gap-3">
            <Link to="/admin/event-hosts" data-testid="admin-event-hosts">
              <Card className="p-4 border-2 border-red-500/60 hover:bg-red-50 transition-colors min-w-[200px]">
                <CreditCard className="text-red-600" size={22} />
                <div className="mt-2 font-semibold">Event Hosts</div>
                <div className="text-sm text-muted-foreground">Manage Stripe accounts</div>
              </Card>
            </Link>
            <Link to="/admin/backup" data-testid="admin-backup">
              <Card className="p-4 border-2 border-red-500/60 hover:bg-red-50 transition-colors min-w-[200px]">
                <Database className="text-red-600" size={22} />
                <div className="mt-2 font-semibold">Database Backup</div>
                <div className="text-sm text-muted-foreground">On-demand BSON dump</div>
              </Card>
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}
